import { apiClient, apiClientPaginated } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface RecipeSummary {
  id: string;
  product_id: string;
  product_name: string;
  name: string;
  yield_quantity: number;
  ingredient_count: number;
  total_cost: number;
  is_active: boolean;
  created_at: string;
}

export interface RecipeIngredient {
  id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  unit: string;
  waste_percentage: number;
  cost_per_unit: number;
  total_cost: number;
}

export interface RecipeDetail {
  id: string;
  tenant_id: string;
  product_id: string;
  product_name: string;
  name: string;
  yield_quantity: number;
  ingredients: RecipeIngredient[];
  total_cost: number;
  cost_per_portion: number;
  instructions: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Ingredient {
  id: string;
  name: string;
  category: string | null;
  unit: string;
  price: number;
  cost: number;
  stock_quantity: number;
  is_active: boolean;
}

export type RecipeUnit = 'KG' | 'G' | 'L' | 'ML' | 'PCS' | 'DOZEN';

interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface RecipeListResponse {
  data: RecipeSummary[];
  meta: PaginationMeta;
}

// Tenant-wide KPI aggregate (GitHub issue #283). Computed server-side across
// every recipe matching the current filters so the KPI cards stay constant as
// you paginate, instead of being reduced from the current 20-row page slice.
export interface RecipeStats {
  total_recipes: number;
  avg_food_cost: number;
  linked_items: number;
}

interface IngredientListResponse {
  data: Ingredient[];
  meta: PaginationMeta;
}

interface CreateRecipeIngredientDto {
  product_id: string;
  quantity: number;
  unit: RecipeUnit;
  waste_percentage?: number;
}

interface CreateRecipeRequest {
  product_id: string;
  name: string;
  yield_quantity?: number;
  ingredients: CreateRecipeIngredientDto[];
  instructions?: string;
}

interface UpdateRecipeRequest {
  name?: string;
  yield_quantity?: number;
  ingredients?: CreateRecipeIngredientDto[];
  instructions?: string;
  is_active?: boolean;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== '',
  );
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

// ─── Service ────────────────────────────────────────────────────────────────

export const recipeService = {
  async listRecipes(params: {
    search?: string;
    product_id?: string;
    category?: string;
    is_active?: boolean;
    page?: number;
    limit?: number;
  } = {}): Promise<RecipeListResponse> {
    return apiClientPaginated<RecipeSummary[]>(`/recipes${buildQuery({
      search: params.search,
      product_id: params.product_id,
      category: params.category,
      is_active: params.is_active,
      page: params.page,
      limit: params.limit,
    })}`);
  },

  async getStats(params: {
    search?: string;
    product_id?: string;
    category?: string;
    is_active?: boolean;
  } = {}): Promise<RecipeStats> {
    return apiClient<RecipeStats>(`/recipes/stats${buildQuery({
      search: params.search,
      product_id: params.product_id,
      category: params.category,
      is_active: params.is_active,
    })}`);
  },

  async getRecipe(id: string): Promise<RecipeDetail> {
    return apiClient<RecipeDetail>(`/recipes/${id}`);
  },

  async createRecipe(dto: CreateRecipeRequest): Promise<RecipeDetail> {
    return apiClient<RecipeDetail>('/recipes', { method: 'POST', body: dto });
  },

  async updateRecipe(id: string, dto: UpdateRecipeRequest): Promise<unknown> {
    return apiClient(`/recipes/${id}`, { method: 'PATCH', body: dto });
  },

  async deleteRecipe(id: string): Promise<unknown> {
    return apiClient(`/recipes/${id}`, { method: 'DELETE' });
  },

  async listIngredients(params: {
    location_id?: string;
    category?: string;
    is_active?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<IngredientListResponse> {
    return apiClientPaginated<Ingredient[]>(`/ingredients${buildQuery({
      location_id: params.location_id,
      category: params.category,
      is_active: params.is_active,
      page: params.page,
      limit: params.limit ?? 200,
    })}`);
  },
};
